/**
 * Gmail-style rich-text email editor (TipTap + ProseMirror).
 *
 * Used by both Compose and Reply. Outputs HTML for storage in
 * messages.html_body, plus a plain-text version in messages.body for
 * fallback rendering / preview.
 *
 * Toolbar: bold, italic, underline, strike, lists (bullet/ordered),
 * link, image (pasted URL or upload), heading, blockquote, undo/redo.
 *
 * Image paste from clipboard works out of the box (TipTap StarterKit
 * + Image extension). Drag-and-drop into the editor also works.
 */
import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon,
  Heading1, Heading2, Undo, Redo, AlignLeft, AlignCenter, AlignRight,
  Trash2, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { uploadFile, makeUploadFilename } from "@/lib/upload";
import { Tooltip, useToast } from "@/components/ui";

interface RichEmailEditorProps {
  value: string;                       // HTML
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  autofocus?: boolean;
  minHeight?: string;                  // CSS height, e.g. "16rem"
  className?: string;
}

export function RichEmailEditor({
  value,
  onChange,
  placeholder = "Write your message…",
  autofocus = false,
  minHeight = "10rem",
  className,
}: RichEmailEditorProps) {
  const toast = useToast();

  /** Shared between paste + drop: upload any image File entries to R2 and
   *  insert each at the current selection. Uses the ProseMirror `view`
   *  directly (no dependency on the React editor instance) so the closure
   *  doesn't need to self-reference its parent useEditor call. */
  const handleImageFiles = (files: File[], view: import("@tiptap/pm/view").EditorView): boolean => {
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return false;
    (async () => {
      for (const file of images) {
        try {
          const filename = makeUploadFilename(file.name || "pasted.png", "img");
          const r = await uploadFile(file, { path: `mail/inline-images/${filename}`, bucket: "public" });
          const imageType = view.state.schema.nodes.image;
          if (!imageType) continue;
          const node = imageType.create({ src: r.publicUrl });
          view.dispatch(view.state.tr.replaceSelectionWith(node));
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "Image upload failed");
        }
      }
    })();
    return true;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-rose-300 underline underline-offset-2 hover:text-rose-200" },
      }),
      Image.configure({
        HTMLAttributes: { class: "rounded-lg max-w-full my-2" },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value,
    autofocus,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none text-zinc-200 prose-headings:text-zinc-100 prose-strong:text-zinc-100 prose-a:text-rose-400 prose-blockquote:text-zinc-300 prose-code:text-zinc-200 prose-li:marker:text-zinc-500 focus:outline-none",
          "[&_p]:my-1.5 [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base",
          "[&_ul]:list-disc [&_ol]:list-decimal [&_li]:my-0",
          "[&_blockquote]:border-l-2 [&_blockquote]:border-zinc-700 [&_blockquote]:pl-3 [&_blockquote]:text-zinc-400 [&_blockquote]:italic",
          "[&_code]:bg-zinc-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.85em]",
          "px-4 py-3 text-sm text-zinc-100 leading-relaxed"
        ),
        style: `min-height: ${minHeight};`,
      },
      // Paste an image (e.g. screenshot) → upload to R2 → insert.
      handlePaste(view, event) {
        const files = Array.from(event.clipboardData?.files ?? []);
        return handleImageFiles(files, view);
      },
      // Drag-and-drop an image file → same flow.
      handleDrop(view, event) {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []);
        return handleImageFiles(files, view);
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = editor.getText();
      onChange(html, text);
    },
  });

  // Keep editor in sync if `value` changes externally (e.g. AI-drafted reply
  // slammed into the box). Guard with a content compare to avoid feedback.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={cn("rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden flex flex-col", className)}>
      <Toolbar editor={editor} />
      <div className="flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// ─── Toolbar ───────────────────────────────────────────────────────────────

function Toolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  // Hooks must run before any early return (rules of hooks — a hook after a
  // conditional return is the React #310 "blank screen" trap this codebase has
  // hit before). Keep useState above the `if (!editor)` guard.
  const [linkOpen, setLinkOpen] = useState(false);
  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-zinc-800 bg-zinc-950/60 px-1.5 py-1">
      <TbBtn editor={editor} cmd="toggleBold" active="bold" icon={Bold} title="Bold (⌘B)" />
      <TbBtn editor={editor} cmd="toggleItalic" active="italic" icon={Italic} title="Italic (⌘I)" />
      <TbBtn editor={editor} cmd="toggleUnderline" active="underline" icon={UnderlineIcon} title="Underline (⌘U)" />
      <TbBtn editor={editor} cmd="toggleStrike" active="strike" icon={Strikethrough} title="Strikethrough" />

      <Divider />

      <TbBtn editor={editor} cmd={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={{ name: "heading", attrs: { level: 1 } }} icon={Heading1} title="Heading 1" />
      <TbBtn editor={editor} cmd={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={{ name: "heading", attrs: { level: 2 } }} icon={Heading2} title="Heading 2" />

      <Divider />

      <TbBtn editor={editor} cmd="toggleBulletList" active="bulletList" icon={List} title="Bullet list" />
      <TbBtn editor={editor} cmd="toggleOrderedList" active="orderedList" icon={ListOrdered} title="Numbered list" />
      <TbBtn editor={editor} cmd="toggleBlockquote" active="blockquote" icon={Quote} title="Quote" />

      <Divider />

      <TbBtn editor={editor} cmd={() => editor.chain().focus().setTextAlign("left").run()}
        active={{ textAlign: "left" }} icon={AlignLeft} title="Align left" />
      <TbBtn editor={editor} cmd={() => editor.chain().focus().setTextAlign("center").run()}
        active={{ textAlign: "center" }} icon={AlignCenter} title="Align center" />
      <TbBtn editor={editor} cmd={() => editor.chain().focus().setTextAlign("right").run()}
        active={{ textAlign: "right" }} icon={AlignRight} title="Align right" />

      <Divider />

      <LinkButton editor={editor} open={linkOpen} setOpen={setLinkOpen} />
      <ImageButton editor={editor} />

      <Divider />

      <TbBtn editor={editor} cmd={() => editor.chain().focus().undo().run()} icon={Undo} title="Undo (⌘Z)" />
      <TbBtn editor={editor} cmd={() => editor.chain().focus().redo().run()} icon={Redo} title="Redo (⌘⇧Z)" />

      <div className="ml-auto pr-1">
        <Tooltip content="Clear all formatting and content">
          <button
            type="button"
            onClick={() => editor.chain().focus().clearContent().run()}
            className="h-7 w-7 grid place-items-center text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 rounded-md"
            aria-label="Clear"
          ><Trash2 className="h-3.5 w-3.5" /></button>
        </Tooltip>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-5 w-px bg-zinc-800 mx-0.5" />;
}

type Editor = ReturnType<typeof useEditor>;

function TbBtn({
  editor,
  cmd,
  active,
  icon: Icon,
  title,
}: {
  editor: Editor;
  cmd: string | (() => void);
  active?: string | Record<string, unknown>;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  if (!editor) return null;
  const isActive = (() => {
    if (!active) return false;
    if (typeof active === "string") return editor.isActive(active);
    if ("name" in active && typeof active.name === "string") {
      return editor.isActive(active.name, active.attrs as Record<string, unknown> | undefined);
    }
    return editor.isActive(active as Record<string, unknown>);
  })();
  return (
    <Tooltip content={title}>
      <button
        type="button"
        onClick={() => {
          if (typeof cmd === "string") {
            const chain = editor.chain().focus() as unknown as Record<string, () => { run: () => void }>;
            chain[cmd]?.()?.run();
          } else {
            cmd();
          }
        }}
        className={cn(
          "h-7 w-7 grid place-items-center rounded-md transition-colors",
          isActive
            ? "bg-rose-500/20 text-rose-200"
            : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
        )}
        aria-label={title}
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function LinkButton({ editor, open, setOpen }: { editor: Editor; open: boolean; setOpen: (v: boolean) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  if (!editor) return null;
  const isLink = editor.isActive("link");

  return (
    <div className="relative">
      <Tooltip content={isLink ? "Edit link" : "Add link"}>
        <button
          type="button"
          onClick={() => {
            const prev = editor.getAttributes("link").href as string | undefined;
            setOpen(true);
            setTimeout(() => { if (inputRef.current) { inputRef.current.value = prev ?? ""; inputRef.current.focus(); } }, 10);
          }}
          className={cn(
            "h-7 w-7 grid place-items-center rounded-md transition-colors",
            isLink ? "bg-rose-500/20 text-rose-200" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          )}
          aria-label={isLink ? "Edit link" : "Add link"}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-30 w-72 rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl p-2">
          <input
            ref={inputRef}
            placeholder="https://…"
            className="w-full h-8 px-2 rounded-md bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-rose-500/50 focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const url = (e.target as HTMLInputElement).value.trim();
                if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                else editor.chain().focus().unsetLink().run();
                setOpen(false);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
          />
          <div className="flex gap-1 mt-1.5">
            <button
              type="button"
              onClick={() => {
                const url = inputRef.current?.value.trim();
                if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                setOpen(false);
              }}
              className="flex-1 h-7 px-2 rounded-md bg-rose-500 text-white text-[11px] font-medium hover:bg-rose-400"
            >Apply</button>
            <button
              type="button"
              onClick={() => { editor.chain().focus().unsetLink().run(); setOpen(false); }}
              className="flex-1 h-7 px-2 rounded-md bg-zinc-800 text-zinc-200 text-[11px] hover:bg-zinc-700"
            >Remove</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageButton({ editor }: { editor: Editor }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  if (!editor) return null;

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setUploading(true);
    try {
      const filename = makeUploadFilename(file.name, "img");
      const result = await uploadFile(file, {
        path: `mail/inline-images/${filename}`,
        bucket: "public",
      });
      editor.chain().focus().setImage({ src: result.publicUrl }).run();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await handleFile(file);
          e.target.value = "";
        }}
      />
      <Tooltip content={uploading ? "Uploading…" : "Insert image (uploads to R2)"}>
        <button
          type="button"
          disabled={uploading}
          onClick={() => {
            const url = window.prompt("Paste image URL — or cancel to upload from your computer");
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            } else {
              fileRef.current?.click();
            }
          }}
          className={cn(
            "h-7 w-7 grid place-items-center rounded-md transition-colors",
            uploading
              ? "text-rose-300 bg-rose-500/10 cursor-wait"
              : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          )}
          aria-label="Insert image"
        >
          {uploading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <ImageIcon className="h-3.5 w-3.5" />}
        </button>
      </Tooltip>
    </>
  );
}
